import { diContainer } from '../DIContainer';
import { AppStorageKey } from '../types';
import FiscalServiceElectron from './FiscalServiceElectron';
import FiscalServiceAndroid from './FiscalServiceAndroid';
import FiscalServiceWeb from './FiscalServiceWeb';
import Store from '../wrappers/Store';
import { I18n } from '../wrappers/I18n';
import Settings from '../wrappers/Settings';
import Device from '../utilities/Device';
import Money from '../utilities/Money';
import JobQueue from '../utilities/JobQueue';
import moment from 'moment-timezone';
import { apiService } from '../services';
import { appStorage } from '../utilities/AppStorage';
class FiscalService {
    service;
    currentTaxRates;
    allTaxRates;
    queue;
    esdcUrl;
    useDevEsdc = false;
    preferredSdc = null;
    isCertificateSetup = false;
    certExpDate = null;
    esdcCertExpDate = null;
    sdcStatus = null;
    statusPollingInterval = null;
    constructor() {
        this.service = null;
        this.esdcUrl = null;
        this.currentTaxRates = null;
        this.allTaxRates = [];
        this.queue = new JobQueue(250, 60000);
    }
    async init(options) {
        const venue = Store.get('tenant').venue;
        const noncashOnly = venue?.settings?.noncash_only;
        const appStorage = diContainer.resolve('appStorage');
        // Explicit null prefix — the backoffice flag is APP-scoped: this init runs
        // inside setTenant → startTenantServices, so the default prefix factory
        // would read `<venue.hash>-backoffice` while initializeAdminApp wrote the
        // bare key — isAdmin was always null here and admin (sr tenants) started
        // the SDC poller it was written to exclude. Pinned by FiscalServiceInit.spec.
        const isAdmin = await appStorage.local().getItem(AppStorageKey.backoffice, null);
        if (I18n.c('order.registerMethod') !== 'taxcore' || !venue || noncashOnly || isAdmin)
            return;
        let savedEsdcUrl = await this.getEsdcUrl();
        if (!savedEsdcUrl) {
            savedEsdcUrl = Settings.env.ESDC_URL;
            await appStorage.local().setItem(AppStorageKey.sdcUrl, savedEsdcUrl, null);
        }
        this.esdcUrl = options?.esdcUrl || savedEsdcUrl;
        this.useDevEsdc = Settings.env.ENV !== 'production' && !venue?.settings?.dev_esdc_disable;
        this.preferredSdc = await this.getPreferredSdc();
        const certificate = await appStorage.native().getItem(AppStorageKey.sdcCert);
        const certificateAlias = await appStorage.native().getItem(AppStorageKey.sdcCertAlias);
        this.isCertificateSetup = !!certificate || !!certificateAlias;
        this.certExpDate = await appStorage.native().getItem(AppStorageKey.sdcCertExpirationDate);
        this.esdcCertExpDate = await appStorage.native().getItem(AppStorageKey.esdcCertExpirationDate);
        this.service = this.createPlatformAdapter();
        await this.service.init({ esdcUrl: this.esdcUrl });
        const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
        await delay(1000);
        await this.refreshSdcStatus();
        await this.getUiData();
        // Clear existing interval if re-initializing
        if (this.statusPollingInterval)
            clearInterval(this.statusPollingInterval);
        this.statusPollingInterval = setInterval(() => this.refreshSdcStatus(), 60 * 1000);
    }
    /**
     * Android and iOS share the mobile adapter: it is fully plugin-driven
     * (capacitor-sdc implements the same contract on both platforms). On iOS
     * the adapter's supported-device check exits init() early — same as any
     * non-ZCS/Ciontek Android tablet: local ESDC stays blocked while VSDC and
     * remote-ESDC calls proceed (they bypass the useLocalEsdc guards).
     */
    createPlatformAdapter() {
        if (Device.isElectron())
            return new FiscalServiceElectron();
        if (Device.isMobile())
            return new FiscalServiceAndroid();
        return new FiscalServiceWeb();
    }
    async refreshSdcStatus(sdc) {
        this.sdcStatus = await this.checkSdcStatus(sdc);
    }
    async verifyPin(pin, sdc) {
        if (!this.service)
            return { success: false, error: { code: 'not_initialized', message: I18n.t(`sdc.status.not_initialized`) } };
        return this.queue.addJob(async () => {
            const sdcType = this.getSdcType(sdc);
            const result = await this.service.verifyPin(pin, sdcType);
            const error = this.checkForError(result, sdcType, 'verifyPin');
            return {
                success: result.data == '0100' || result.data == '"0100"',
                data: result.data,
                error
            };
        });
    }
    async createInvoice(invoice, sdc) {
        if (!this.service)
            return { success: false, error: { code: 'not_initialized', message: I18n.t(`sdc.status.not_initialized`) } };
        return this.queue.addJob(async () => {
            const sdcType = this.getSdcType(sdc);
            const result = await this.service.createInvoice(invoice, sdcType);
            const error = this.checkForError(result, sdcType, 'createInvoice');
            return {
                success: !!result.data && result.data.messages === 'Success',
                data: result.data,
                error
            };
        });
    }
    async getInvoice(requestId, sdc) {
        if (!this.service)
            return { success: false, error: { code: 'not_initialized', message: I18n.t(`sdc.status.not_initialized`) } };
        return this.queue.addJob(async () => {
            const sdcType = this.getSdcType(sdc);
            const result = await this.service.getInvoice(requestId, sdcType);
            const error = this.checkForError(result, sdcType, 'getInvoice');
            return {
                success: !!result.data && result.data.messages === 'Success',
                data: result.data,
                error
            };
        });
    }
    async getEnvironmentParameters(sdc) {
        if (!this.service)
            return { success: false, error: { code: 'not_initialized', message: I18n.t(`sdc.status.not_initialized`) } };
        return this.queue.addJob(async () => {
            const sdcType = this.getSdcType(sdc);
            const result = await this.service.getEnvironmentParameters(sdcType);
            const error = this.checkForError(result, sdcType, 'getEnvironmentParameters');
            return {
                success: !error,
                data: result.data,
                error
            };
        });
    }
    async getStatus(sdc) {
        if (!this.service)
            return { success: false, error: { code: 'not_initialized', message: I18n.t(`sdc.status.not_initialized`) } };
        return this.queue.addJob(async () => {
            const sdcType = this.getSdcType(sdc);
            const result = await this.service.getStatus(sdcType);
            const error = this.checkForError(result, sdcType, 'getStatus');
            if (!error) {
                const isProductionApiUrl = apiService.getBaseUrl()?.startsWith('https://api.');
                if (Settings.env.ENV !== 'production' && isProductionApiUrl) {
                    this.manuallyLoadProductionTaxRates();
                }
                else if (result.data?.currentTaxRates && result.data?.allTaxRates?.length) {
                    this.currentTaxRates = result.data.currentTaxRates;
                    this.allTaxRates = result.data.allTaxRates;
                    await appStorage.local().setItem(AppStorageKey.sdcTaxRates, { currentTaxRates: this.currentTaxRates, allTaxRates: this.allTaxRates });
                }
            }
            if (!this.currentTaxRates || !this.allTaxRates.length) {
                const savedTaxRates = await appStorage.local().getItem(AppStorageKey.sdcTaxRates);
                if (savedTaxRates) {
                    this.currentTaxRates = savedTaxRates.currentTaxRates;
                    this.allTaxRates = savedTaxRates.allTaxRates;
                }
            }
            return {
                success: !error,
                data: result.data,
                error
            };
        });
    }
    async getAttention(sdc) {
        if (!this.service)
            return { success: false, error: { code: 'not_initialized', message: I18n.t(`sdc.status.not_initialized`) } };
        return this.queue.addJob(async () => {
            const sdcType = this.getSdcType(sdc);
            const result = await this.service.getAttention(sdcType);
            const error = this.checkForError(result, sdcType, 'getAttention');
            return {
                success: !error,
                data: result.data,
                error
            };
        });
    }
    async getUiData(sdc) {
        if (!this.service)
            return { success: false, error: { code: 'not_initialized', message: I18n.t(`sdc.status.not_initialized`) } };
        return this.queue.addJob(async () => {
            const sdcType = this.getSdcType(sdc);
            const result = await this.service.getUiData(sdcType);
            const error = this.checkForError(result, sdcType, 'getUiData');
            if (result.data?.certExpDate) {
                const appStorage = diContainer.resolve('appStorage');
                await appStorage.native().setItem(AppStorageKey.esdcCertExpirationDate, result.data?.certExpDate);
            }
            if (result.data && (typeof result.data.taxpayer === 'string')) {
                try {
                    result.data.taxpayer = JSON.parse(result.data.taxpayer);
                }
                catch (error) {
                    console.log('taxpayer was a string but could not be parsed as a valid JSON object');
                }
            }
            return {
                success: !error,
                data: result.data,
                error
            };
        });
    }
    async storeLocalAuditFiles(sdc) {
        if (!this.service)
            return { success: false, error: { code: 'not_initialized', message: I18n.t(`sdc.status.not_initialized`) } };
        return this.queue.addJob(async () => {
            const sdcType = this.getSdcType(sdc);
            const result = await this.service.storeLocalAuditFiles(sdcType);
            const error = this.checkForError(result, sdcType, 'storeLocalAuditFiles');
            return {
                success: !error,
                data: result.data,
                error
            };
        });
    }
    async executeCommandsFromStorage(sdc) {
        if (!this.service)
            return { success: false, error: { code: 'not_initialized', message: I18n.t(`sdc.status.not_initialized`) } };
        return this.queue.addJob(async () => {
            const sdcType = this.getSdcType(sdc);
            const result = await this.service.executeCommandsFromStorage(sdcType);
            const error = this.checkForError(result, sdcType, 'executeCommandsFromStorage');
            return {
                success: !error,
                data: result.data,
                error
            };
        });
    }
    async sendPendingAuditPackages(sdc) {
        if (!this.service)
            return { success: false, error: { code: 'not_initialized', message: I18n.t(`sdc.status.not_initialized`) } };
        return this.queue.addJob(async () => {
            const sdcType = this.getSdcType(sdc);
            const result = await this.service.sendPendingAuditPackages(sdcType);
            const error = this.checkForError(result, sdcType, 'sendPendingAuditPackages');
            return {
                success: !error,
                data: result.data,
                error
            };
        });
    }
    async reset(sdc) {
        if (!this.service)
            return { success: false, error: { code: 'not_initialized', message: I18n.t(`sdc.status.not_initialized`) } };
        return this.queue.addJob(async () => {
            const sdcType = this.getSdcType(sdc);
            const result = await this.service.reset(sdcType);
            const error = this.checkForError(result, sdcType, 'reset');
            return {
                success: !error,
                data: result.data,
                error
            };
        });
    }
    async chooseCertificate(password, pac) {
        if (!this.service)
            return { success: false };
        return this.queue.addJob(async () => {
            const chooseCertificateResult = await this.service.chooseCertificate(password, pac);
            if (chooseCertificateResult.success) {
                this.isCertificateSetup = true;
                this.certExpDate = chooseCertificateResult.expirationDate || null;
            }
            return chooseCertificateResult;
        });
    }
    async deleteCertificate() {
        const appStorage = diContainer.resolve('appStorage');
        await appStorage.native().deleteItem(AppStorageKey.sdcCertPac);
        await appStorage.native().deleteItem(AppStorageKey.sdcCert);
        await appStorage.native().deleteItem(AppStorageKey.sdcCertKey);
        await appStorage.native().deleteItem(AppStorageKey.sdcCertAlias);
        await appStorage.native().deleteItem(AppStorageKey.sdcCertExpirationDate);
        this.isCertificateSetup = false;
        this.certExpDate = null;
    }
    async checkSdcStatus(sdc) {
        if (!this.service)
            return { success: false, error: { code: 'not_initialized', message: I18n.t(`sdc.status.not_initialized`) } };
        const sdcType = this.getSdcType(sdc);
        const sdcStatus = await this.getStatus(sdcType);
        // if device is in sleep mode, ignore the check.. just return last known status
        if (sdcStatus.error?.code === 'device_sleeping') {
            return this.sdcStatus ?? { success: true };
        }
        // If no SDC was found
        if (!sdcStatus.success) {
            const errorCode = Device.isWeb() ? 'not_possible_from_web_app' : 'not_found';
            return { success: false, error: { code: errorCode, message: I18n.t(`sdc.status.${errorCode}`) } };
        }
        const statusCodes = sdcStatus.data?.gsc;
        // If VSDC is used
        if (!statusCodes) {
            return { success: true };
        }
        // If ESDC is used
        const statuses = statusCodes.map(statusCode => this.statuses('getStatus').find(status => status.code === statusCode))
            .filter((s) => s !== undefined);
        const warnings = statuses.filter(st => !!st && st.level === 'warning');
        const errors = statuses.filter(st => !!st && st.level === 'error');
        if (errors.length) {
            return { success: false, error: { code: errors[0].label, message: errors[0].message } };
        }
        const smartCardNotPresent = warnings.find(error => error.label === 'smart_card_not_present');
        if (smartCardNotPresent) {
            return { success: false, error: { code: smartCardNotPresent.label, message: smartCardNotPresent.message } };
        }
        if (sdcStatus.data?.isPinRequired) {
            const appStorage = diContainer.resolve('appStorage');
            const savedPin = await appStorage.native().getItem(AppStorageKey.sdcPin);
            if (savedPin) {
                const result = await this.verifyPin(savedPin, sdcType);
                // allow the SDC to initialize
                const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
                await delay(1000);
                if (result.success) {
                    return this.checkSdcStatus(sdc);
                }
                await appStorage.native().deleteItem(AppStorageKey.sdcPin);
            }
            return { success: false, error: { code: 'pin_required', message: I18n.t('sdc.status.pin_required') } };
        }
        return sdcStatus;
    }
    getSdcType(sdc) {
        if (sdc)
            return sdc;
        if (this.preferredSdc === 'VSDC')
            return 'VSDC';
        if (this.preferredSdc === 'ESDC')
            return this.useDevEsdc ? 'DEVSDC' : 'ESDC';
        if (this.useDevEsdc)
            return 'DEVSDC';
        const certificateValid = this.certExpDate ? moment(this.certExpDate).diff(moment(), 'days') > 0 : false;
        const certificateUsable = Store.get('isOnline') && this.isCertificateSetup && certificateValid;
        return certificateUsable ? 'VSDC' : 'ESDC';
    }
    /**
     * Calculate taxes for the invoice
     *
     * @param {Object} invoice Invoice request coming from POS
     * @returns {Array} Array of the tax items
     */
    calculateTaxes(invoice) {
        if (!this.currentTaxRates)
            return null;
        // Choose tax rate group that needs to be applied
        let appliedTaxRateGroup = this.currentTaxRates;
        if ((invoice.transactionType === 'Refund' || invoice.invoiceType === 'Copy') && !!invoice.referentDocumentDT && !!invoice.referentDocumentNumber) {
            const allTaxRatesOrdered = this.allTaxRates.sort((a, b) => new Date(a.validFrom).getTime() - new Date(b.validFrom).getTime());
            const refDate = new Date(invoice.referentDocumentDT);
            for (let i = 0; i < allTaxRatesOrdered.length; i++) {
                const validFrom = new Date(allTaxRatesOrdered[i].validFrom);
                if (refDate.getTime() >= validFrom.getTime()) {
                    appliedTaxRateGroup = allTaxRatesOrdered[i];
                }
            }
        }
        const allTaxItems = [];
        invoice.items.forEach(item => {
            let totalAmount = item.totalAmount;
            const itemTaxRates = item.labels
                .map(label => {
                const taxCategory = appliedTaxRateGroup.taxCategories.find(category => {
                    return category.taxRates.map(rate => rate.label).includes(label);
                });
                const taxRate = taxCategory.taxRates.find(rate => rate.label === label);
                return { ...taxRate, categoryType: taxCategory.categoryType, categoryName: taxCategory.name };
            });
            itemTaxRates
                .filter(taxRate => taxRate.categoryType === 'AmountPerQuantity' || taxRate.categoryType == 2)
                .forEach(taxRate => {
                const rateItem = this.calculateTaxItem(item, taxRate, itemTaxRates, totalAmount);
                totalAmount = Money.parse(totalAmount).subtract(Money.parse(rateItem.amount)).toRoundedUnit(4);
                allTaxItems.push(rateItem);
            });
            itemTaxRates
                .filter(taxRate => taxRate.categoryType !== 'AmountPerQuantity' && taxRate.categoryType != 2)
                .forEach(taxRate => {
                allTaxItems.push(this.calculateTaxItem(item, taxRate, itemTaxRates, totalAmount));
            });
        });
        // Sum tax items for the same labels
        const distinctLabels = allTaxItems.map(item => item.label)
            .filter((label, index, array) => index === array.indexOf(label));
        const taxItems = distinctLabels.map(label => {
            const labelTaxItems = allTaxItems.filter(item => item.label === label);
            const labelTaxItemsAmount = labelTaxItems.reduce((acc, cur) => acc.add(Money.parse(cur.amount || 0)), Money.parse(0));
            return { ...labelTaxItems[0], amount: labelTaxItemsAmount.toRoundedUnit(4) };
        });
        // Sum tax amounts for label's categories
        const taxCategories = [];
        taxItems.forEach(taxItem => {
            const taxCategory = appliedTaxRateGroup.taxCategories.find(category => {
                return category.taxRates.map(rate => rate.label).includes(taxItem.label);
            });
            const addedTaxCategory = taxCategories.find(category => taxCategory?.orderId && category.orderId === taxCategory.orderId);
            if (!addedTaxCategory) {
                taxCategories.push({ ...taxCategory, amount: taxItem.amount });
            }
            else {
                addedTaxCategory.amount = Money.parse(addedTaxCategory.amount)
                    .add(Money.parse(taxItem.amount))
                    .toRoundedUnit(4);
            }
        });
        return {
            taxItems: taxItems,
            taxCategories: taxCategories,
            taxGroupRevision: appliedTaxRateGroup.groupId,
        };
    }
    /**
     * Calculate taxes for a single invoice item
     *
     * @param {Object} item Invoice item
     * @param {Object} taxRate Tax rate object for that item from TaxCore API
     * @param {Array} itemTaxRates All tax rates applied to that item (each including tax category)
     * @param {number} totalAmount Total item amount used for tax calculation
     * @returns {Object} Tax item object
     */
    calculateTaxItem(item, taxRate, itemTaxRates, totalAmount) {
        const amount = Money.parse(totalAmount || item.totalAmount);
        const result = {
            amount: undefined,
            categoryType: 0,
            categoryName: taxRate.categoryName,
            label: taxRate.label,
            rate: taxRate.rate,
        };
        // If label is in category AmountPerQuantity
        if (taxRate.categoryType === 'AmountPerQuantity' || taxRate.categoryType == 2) {
            result.categoryType = 2;
            result.amount = Money.parse(taxRate.rate)
                .multiply(item.quantity)
                .toRoundedUnit(4);
            return result;
        }
        const taxRatesOnNet = itemTaxRates.filter(rate => rate.categoryType === 'TaxOnNet' || rate.categoryType == 0);
        const taxRatesOnNetSum = taxRatesOnNet.reduce((acc, cur) => acc.add(Money.parse(cur.rate)), Money.parse(0));
        const taxRatesOnTotal = itemTaxRates.filter(rate => rate.categoryType === 'TaxOnTotal' || rate.categoryType == 1);
        const taxRatesOnTotalSum = taxRatesOnTotal.reduce((acc, cur) => acc.add(Money.parse(cur.rate)), Money.parse(0));
        // If item has no labels in category TaxOnTotal
        if (!taxRatesOnTotal.length) {
            result.categoryType = 0;
            result.amount = amount
                .multiply(taxRate.rate)
                .divide(Money.parse(100).add(taxRatesOnNetSum).toUnit())
                .toRoundedUnit(4);
            return result;
        }
        // If item has labels in category TaxOnTotal and this label is in that category
        if (taxRate.categoryType === 'TaxOnTotal' || taxRate.categoryType == 1) {
            result.categoryType = 1;
            result.amount = amount
                .divide(Money.parse(1).add(taxRatesOnTotalSum.divide(100)).toUnit())
                .multiply(taxRate.rate)
                .divide(100)
                .toRoundedUnit(4);
            return result;
        }
        // If item has labels in category TaxOnTotal and this label is not in that category
        result.categoryType = 0;
        result.amount = amount
            .divide(Money.parse(1).add(taxRatesOnTotalSum.divide(100)).toUnit())
            .multiply(taxRate.rate)
            .divide(Money.parse(100).add(taxRatesOnNetSum).toUnit())
            .toRoundedUnit(4);
        return result;
    }
    checkForError(result, sdcType, method) {
        let error = undefined;
        const errorFromData = this.statuses(method).find(status => status.level === 'error' && status.code === String(result.data));
        let errorData = result.error?.data;
        if (typeof errorData === 'string') {
            try {
                errorData = JSON.parse(errorData);
            }
            catch { }
        }
        const modelState = result.data?.modelState || errorData?.modelState;
        if (modelState) {
            error = {
                code: 'receipt_data_not_valid',
                message: I18n.t('sdc.status.receipt_data_not_valid'),
                data: { modelState }
            };
        }
        else if (errorFromData) {
            error = { code: errorFromData.label, message: errorFromData.message };
        }
        else if (result.error) {
            const errorStatus = this.statuses(method).find(status => status.level === 'error' && [result.error?.code, String(result.error?.data)].includes(status.code));
            if (errorStatus) {
                error = { code: errorStatus.label, message: errorStatus.message };
            }
            else {
                const isTimeoutError = /timeout|timed out|failed to connect.*after \d+ms/i.test(result.error.message || '');
                const code = isTimeoutError ? 'timeout' : result.error.code;
                const translationKey = `sdc.status.${code}`;
                const translatedValue = I18n.t(translationKey);
                error = {
                    code,
                    message: (translatedValue !== translationKey ? translatedValue : result.error.message) || result.error.code,
                    data: result.error.data
                };
            }
        }
        if (error)
            console.log(`${sdcType} response error: ` + error.code);
        return error;
    }
    statuses(method) {
        const isCardErrorBlocking = ['verifyPin', 'createInvoice', 'getEnvironmentParameters'].includes(method);
        return [
            { code: '0000', level: 'info', label: 'ok', message: I18n.t('sdc.status.ok') },
            { code: '0100', level: 'info', label: 'pin_valid', message: I18n.t('sdc.status.pin_valid') },
            { code: '0210', level: 'info', label: 'internet_available', message: I18n.t('sdc.status.internet_available') },
            { code: '0220', level: 'info', label: 'internet_unavailable', message: I18n.t('sdc.status.internet_unavailable') },
            { code: '1100', level: 'warning', label: 'storage_full', message: I18n.t('sdc.status.storage_full') },
            { code: '1300', level: !isCardErrorBlocking ? 'warning' : 'error', label: 'smart_card_not_present', message: I18n.t('sdc.status.smart_card_not_present') },
            { code: '1400', level: 'warning', label: 'audit_requred', message: I18n.t('sdc.status.audit_requred') },
            { code: '1500', level: !isCardErrorBlocking ? 'warning' : 'error', label: 'pin_required', message: I18n.t('sdc.status.pin_required') },
            { code: '1999', level: 'warning', label: 'unidentified_warning', message: I18n.t('sdc.status.unidentified_warning') },
            { code: '2100', level: 'error', label: 'pin_invalid', message: I18n.t('sdc.status.pin_invalid') },
            { code: '2110', level: 'error', label: 'card_locked', message: I18n.t('sdc.status.card_locked') },
            { code: '2210', level: 'error', label: 'se_locked', message: I18n.t('sdc.status.se_locked') },
            { code: '2220', level: 'error', label: 'se_communication_failed', message: I18n.t('sdc.status.se_communication_failed') },
            { code: '2230', level: 'error', label: 'se_protocol_mismatch', message: I18n.t('sdc.status.se_protocol_mismatch') },
            { code: '2310', level: 'error', label: 'invalid_tax_labels', message: I18n.t('sdc.status.invalid_tax_labels') },
            { code: '2400', level: 'error', label: 'not_configured', message: I18n.t('sdc.status.not_configured') },
            { code: '2800', level: 'error', label: 'field_required', message: I18n.t('sdc.status.field_required') },
            { code: '2801', level: 'error', label: 'field_value_too_long', message: I18n.t('sdc.status.field_value_too_long') },
            { code: '2802', level: 'error', label: 'field_value_too_short', message: I18n.t('sdc.status.field_value_too_short') },
            { code: '2803', level: 'error', label: 'invalid_field_length', message: I18n.t('sdc.status.invalid_field_length') },
            { code: '2804', level: 'error', label: 'field_out_of_range', message: I18n.t('sdc.status.field_out_of_range') },
            { code: '2805', level: 'error', label: 'invalid_field_value', message: I18n.t('sdc.status.invalid_field_value') },
            { code: '2806', level: 'error', label: 'invalid_data_format', message: I18n.t('sdc.status.invalid_data_format') },
            { code: '2807', level: 'error', label: 'list_too_short', message: I18n.t('sdc.status.list_too_short') },
            { code: '2808', level: 'error', label: 'list_too_long', message: I18n.t('sdc.status.list_too_long') },
            { code: '4230', level: 'error', label: 'license_not_valid', message: I18n.t('sdc.status.license_not_valid') },
            { code: '4242', level: 'error', label: 'duplicated_invoice_request', message: I18n.t('sdc.status.duplicated_invoice_request') },
            { code: '6882', level: 'error', label: 'secure_messaging_not_supported', message: I18n.t('sdc.status.secure_messaging_not_supported') },
            { code: '7000', level: 'error', label: 'auth_failure', message: I18n.t('sdc.status.auth_failure') },
            { code: '9001', level: 'error', label: 'device_sleeping', message: I18n.t('sdc.status.device_sleeping') },
        ];
    }
    /**
     * Get ESDC Url
     */
    async getEsdcUrl() {
        const appStorage = diContainer.resolve('appStorage');
        return appStorage.local().getItem(AppStorageKey.sdcUrl, null);
    }
    /**
     * Set ESDC Url
     */
    async setEsdcUrl(url) {
        this.esdcUrl = url;
        const appStorage = diContainer.resolve('appStorage');
        await appStorage.local().setItem(AppStorageKey.sdcUrl, url, null);
        if (!this.service)
            return;
        await this.service.init({ esdcUrl: url });
    }
    /**
     * Remove ESDC Url
     */
    removeEsdcUrl() {
        const appStorage = diContainer.resolve('appStorage');
        appStorage.local().deleteItem(AppStorageKey.sdcUrl, null);
    }
    /**
     * Set preferred SDC type
     */
    async setPreferredSdc(sdc) {
        this.preferredSdc = sdc;
        const appStorage = diContainer.resolve('appStorage');
        await appStorage.local().setItem(AppStorageKey.sdcPreferred, sdc);
    }
    /**
     * Get preferred SDC type
     */
    async getPreferredSdc() {
        const appStorage = diContainer.resolve('appStorage');
        return appStorage.local().getItem(AppStorageKey.sdcPreferred);
    }
    /**
     * Remove preferred SDC type
     */
    removePreferredSdc() {
        const appStorage = diContainer.resolve('appStorage');
        appStorage.local().deleteItem(AppStorageKey.sdcPreferred);
    }
    /**
     * Load DEV mode data
     */
    manuallyLoadProductionTaxRates() {
        this.currentTaxRates = {
            validFrom: '2021-11-01T01:00:00Z',
            groupId: 1,
            taxCategories: [
                {
                    name: 'Без ПДВ',
                    categoryType: 0,
                    taxRates: [{ rate: 0, label: 'Г' }],
                    orderId: 4
                },
                {
                    name: 'Није у ПДВ',
                    categoryType: 0,
                    taxRates: [{ rate: 0, label: 'А' }],
                    orderId: 1
                },
                {
                    name: 'О-ПДВ',
                    categoryType: 0,
                    taxRates: [{ rate: 20, label: 'Ђ' }],
                    orderId: 2
                },
                {
                    name: 'П-ПДВ',
                    categoryType: 0,
                    taxRates: [{ rate: 10, label: 'Е' }],
                    orderId: 3
                }
            ]
        };
        this.allTaxRates = [
            {
                validFrom: '2021-11-01T01:00:00Z',
                groupId: 1,
                taxCategories: [
                    {
                        name: 'Без ПДВ',
                        categoryType: 0,
                        taxRates: [{ rate: 0, label: 'Г' }],
                        orderId: 4
                    },
                    {
                        name: 'Није у ПДВ',
                        categoryType: 0,
                        taxRates: [{ rate: 0, label: 'А' }],
                        orderId: 1
                    },
                    {
                        name: 'О-ПДВ',
                        categoryType: 0,
                        taxRates: [{ rate: 20, label: 'Ђ' }],
                        orderId: 2
                    },
                    {
                        name: 'П-ПДВ',
                        categoryType: 0,
                        taxRates: [{ rate: 10, label: 'Е' }],
                        orderId: 3
                    }
                ]
            },
            {
                validFrom: '2013-12-31T23:00:00Z',
                groupId: 6,
                taxCategories: [
                    {
                        name: 'Без ПДВ',
                        categoryType: 0,
                        taxRates: [{ rate: 0, label: 'Г' }],
                        orderId: 4
                    },
                    {
                        name: 'Није у ПДВ',
                        categoryType: 0,
                        taxRates: [{ rate: 0, label: 'А' }],
                        orderId: 1
                    },
                    {
                        name: 'О-ПДВ',
                        categoryType: 0,
                        taxRates: [{ rate: 20, label: 'Ђ' }],
                        orderId: 2
                    },
                    {
                        name: 'П-ПДВ',
                        categoryType: 0,
                        taxRates: [{ rate: 10, label: 'Е' }],
                        orderId: 3
                    }
                ]
            },
            {
                validFrom: '2012-09-30T22:00:00Z',
                groupId: 3,
                taxCategories: [
                    {
                        name: 'Без ПДВ',
                        categoryType: 0,
                        taxRates: [{ rate: 0, label: 'Г' }],
                        orderId: 4
                    },
                    {
                        name: 'Није у ПДВ',
                        categoryType: 0,
                        taxRates: [{ rate: 0, label: 'А' }],
                        orderId: 1
                    },
                    {
                        name: 'О-ПДВ',
                        categoryType: 0,
                        taxRates: [{ rate: 20, label: 'Ђ' }],
                        orderId: 2
                    },
                    {
                        name: 'П-ПДВ',
                        categoryType: 0,
                        taxRates: [{ rate: 8, label: 'Е' }],
                        orderId: 3
                    }
                ]
            },
            {
                validFrom: '2004-12-31T23:00:00Z',
                groupId: 2,
                taxCategories: [
                    {
                        name: 'Без ПДВ',
                        categoryType: 0,
                        taxRates: [{ rate: 0, label: 'Г' }],
                        orderId: 4
                    },
                    {
                        name: 'Није у ПДВ',
                        categoryType: 0,
                        taxRates: [{ rate: 0, label: 'А' }],
                        orderId: 1
                    },
                    {
                        name: 'О-ПДВ',
                        categoryType: 0,
                        taxRates: [{ rate: 18, label: 'Ђ' }],
                        orderId: 2
                    },
                    {
                        name: 'П-ПДВ',
                        categoryType: 0,
                        taxRates: [{ rate: 8, label: 'Е' }],
                        orderId: 3
                    }
                ]
            }
        ];
    }
    manuallyLoadStagingTaxRates() {
        this.currentTaxRates = {
            validFrom: '2023-10-08T06:40:00Z',
            groupId: 8,
            taxCategories: [
                {
                    name: 'ECAL',
                    categoryType: 0,
                    taxRates: [{ rate: 11, label: 'F' }],
                    orderId: 1
                },
                {
                    name: 'N-TAX',
                    categoryType: 0,
                    taxRates: [{ rate: 0, label: 'N' }],
                    orderId: 2
                },
                {
                    name: 'PBL',
                    categoryType: 2,
                    taxRates: [{ rate: 0.5, label: 'P' }],
                    orderId: 3
                },
                {
                    name: 'STT',
                    categoryType: 0,
                    taxRates: [{ rate: 6, label: 'E' }],
                    orderId: 4
                },
                {
                    name: 'TOTL',
                    categoryType: 1,
                    taxRates: [{ rate: 2, label: 'T' }],
                    orderId: 5
                },
                {
                    name: 'VAT',
                    categoryType: 0,
                    taxRates: [{ rate: 10, label: 'A' }, { rate: 0, label: 'B' }, { rate: 19, label: 'Ж' }],
                    orderId: 6
                },
                {
                    name: 'VAT-EXCL',
                    categoryType: 0,
                    taxRates: [{ rate: 0, label: 'C' }],
                    orderId: 7
                }
            ]
        };
        this.allTaxRates = [
            {
                validFrom: '2023-10-05T06:45:00Z',
                groupId: 9,
                taxCategories: [
                    {
                        name: 'ECAL',
                        categoryType: 0,
                        taxRates: [{ rate: 11, label: 'F' }],
                        orderId: 1
                    },
                    {
                        name: 'N-TAX',
                        categoryType: 0,
                        taxRates: [{ rate: 0, label: 'N' }],
                        orderId: 2
                    },
                    {
                        name: 'PBL',
                        categoryType: 2,
                        taxRates: [{ rate: 0.5, label: 'P' }],
                        orderId: 3
                    },
                    {
                        name: 'STT',
                        categoryType: 0,
                        taxRates: [{ rate: 6, label: 'E' }],
                        orderId: 4
                    },
                    {
                        name: 'TOTL',
                        categoryType: 1,
                        taxRates: [{ rate: 2, label: 'T' }],
                        orderId: 5
                    },
                    {
                        name: 'VAT',
                        categoryType: 0,
                        taxRates: [{ rate: 11, label: 'A' }, { rate: 0, label: 'B' }, { rate: 19, label: 'Ж' }],
                        orderId: 6
                    },
                    {
                        name: 'VAT-EXCL',
                        categoryType: 0,
                        taxRates: [{ rate: 0, label: 'C' }],
                        orderId: 7
                    }
                ]
            },
            {
                validFrom: '2023-10-08T06:40:00Z',
                groupId: 8,
                taxCategories: [
                    {
                        name: 'ECAL',
                        categoryType: 0,
                        taxRates: [{ rate: 11, label: 'F' }],
                        orderId: 1
                    },
                    {
                        name: 'N-TAX',
                        categoryType: 0,
                        taxRates: [{ rate: 0, label: 'N' }],
                        orderId: 2
                    },
                    {
                        name: 'PBL',
                        categoryType: 2,
                        taxRates: [{ rate: 0.5, label: 'P' }],
                        orderId: 3
                    },
                    {
                        name: 'STT',
                        categoryType: 0,
                        taxRates: [{ rate: 6, label: 'E' }],
                        orderId: 4
                    },
                    {
                        name: 'TOTL',
                        categoryType: 1,
                        taxRates: [{ rate: 2, label: 'T' }],
                        orderId: 5
                    },
                    {
                        name: 'VAT',
                        categoryType: 0,
                        taxRates: [{ rate: 10, label: 'A' }, { rate: 0, label: 'B' }, { rate: 19, label: 'Ж' }],
                        orderId: 6
                    },
                    {
                        name: 'VAT-EXCL',
                        categoryType: 0,
                        taxRates: [{ rate: 0, label: 'C' }],
                        orderId: 7
                    }
                ]
            },
            {
                validFrom: '2023-10-05T06:35:00Z',
                groupId: 7,
                taxCategories: [
                    {
                        name: 'ECAL',
                        categoryType: 0,
                        taxRates: [{ rate: 11, label: 'F' }],
                        orderId: 1
                    },
                    {
                        name: 'N-TAX',
                        categoryType: 0,
                        taxRates: [{ rate: 0, label: 'N' }],
                        orderId: 2
                    },
                    {
                        name: 'PBL',
                        categoryType: 2,
                        taxRates: [{ rate: 0.5, label: 'P' }],
                        orderId: 3
                    },
                    {
                        name: 'STT',
                        categoryType: 0,
                        taxRates: [{ rate: 6, label: 'E' }],
                        orderId: 4
                    },
                    {
                        name: 'TOTL',
                        categoryType: 1,
                        taxRates: [{ rate: 2, label: 'T' }],
                        orderId: 5
                    },
                    {
                        name: 'VAT',
                        categoryType: 0,
                        taxRates: [{ rate: 1000, label: 'A' }, { rate: 0, label: 'B' }, { rate: 19, label: 'Ж' }],
                        orderId: 6
                    },
                    {
                        name: 'VAT-EXCL',
                        categoryType: 0,
                        taxRates: [{ rate: 0, label: 'C' }],
                        orderId: 7
                    }
                ]
            },
            {
                validFrom: '2021-12-19T23:00:00Z',
                groupId: 5,
                taxCategories: [
                    {
                        name: 'ECAL',
                        categoryType: 0,
                        taxRates: [{ rate: 11, label: 'F' }],
                        orderId: 1
                    },
                    {
                        name: 'N-TAX',
                        categoryType: 0,
                        taxRates: [{ rate: 0, label: 'N' }],
                        orderId: 2
                    },
                    {
                        name: 'PBL',
                        categoryType: 2,
                        taxRates: [{ rate: 0.5, label: 'P' }],
                        orderId: 3
                    },
                    {
                        name: 'STT',
                        categoryType: 0,
                        taxRates: [{ rate: 6, label: 'E' }],
                        orderId: 4
                    },
                    {
                        name: 'TOTL',
                        categoryType: 1,
                        taxRates: [{ rate: 2, label: 'T' }],
                        orderId: 5
                    },
                    {
                        name: 'VAT',
                        categoryType: 0,
                        taxRates: [{ rate: 9, label: 'A' }, { rate: 0, label: 'B' }, { rate: 19, label: 'Ж' }],
                        orderId: 6
                    },
                    {
                        name: 'VAT-EXCL',
                        categoryType: 0,
                        taxRates: [{ rate: 0, label: 'C' }],
                        orderId: 7
                    }
                ]
            },
            {
                validFrom: '2021-07-22T09:55:24Z',
                groupId: 2,
                taxCategories: [
                    {
                        name: 'ECAL',
                        categoryType: 0,
                        taxRates: [{ rate: 11, label: 'F' }],
                        orderId: 1
                    },
                    {
                        name: 'N-TAX',
                        categoryType: 0,
                        taxRates: [{ rate: 0, label: 'N' }],
                        orderId: 2
                    },
                    {
                        name: 'PBL',
                        categoryType: 2,
                        taxRates: [{ rate: 0.5, label: 'P' }],
                        orderId: 3
                    },
                    {
                        name: 'STT',
                        categoryType: 0,
                        taxRates: [{ rate: 6, label: 'E' }],
                        orderId: 4
                    },
                    {
                        name: 'TOTL',
                        categoryType: 1,
                        taxRates: [{ rate: 2, label: 'T' }],
                        orderId: 5
                    },
                    {
                        name: 'VAT',
                        categoryType: 0,
                        taxRates: [{ rate: 9, label: 'A' }, { rate: 0, label: 'B' }],
                        orderId: 6
                    },
                    {
                        name: 'VAT-EXCL',
                        categoryType: 0,
                        taxRates: [{ rate: 0, label: 'C' }],
                        orderId: 7
                    }
                ]
            },
            {
                validFrom: '2021-07-15T23:08:09Z',
                groupId: 1,
                taxCategories: [
                    {
                        name: 'ECAL',
                        categoryType: 0,
                        taxRates: [{ rate: 10, label: 'F' }],
                        orderId: 1
                    },
                    {
                        name: 'N-TAX',
                        categoryType: 0,
                        taxRates: [{ rate: 0, label: 'N' }],
                        orderId: 2
                    },
                    {
                        name: 'PBL',
                        categoryType: 2,
                        taxRates: [{ rate: 0.5, label: 'P' }],
                        orderId: 3
                    },
                    {
                        name: 'STT',
                        categoryType: 0,
                        taxRates: [{ rate: 6, label: 'E' }],
                        orderId: 4
                    },
                    {
                        name: 'TOTL',
                        categoryType: 1,
                        taxRates: [{ rate: 2, label: 'T' }],
                        orderId: 5
                    },
                    {
                        name: 'VAT',
                        categoryType: 0,
                        taxRates: [{ rate: 9, label: 'A' }, { rate: 0, label: 'B' }],
                        orderId: 6
                    },
                    {
                        name: 'VAT-EXCL',
                        categoryType: 0,
                        taxRates: [{ rate: 0, label: 'C' }],
                        orderId: 7
                    }
                ]
            }
        ];
    }
}
const fiscalService = new FiscalService();
export { fiscalService };
export default fiscalService;
//# sourceMappingURL=FiscalService.js.map